import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Target,
  ArrowRight,
  ArrowLeft,
  Loader2,
  TrendingUp,
  Users,
  ShoppingCart,
  Eye,
  Megaphone,
  MousePointerClick,
  Check
} from 'lucide-react';
import toast from 'react-hot-toast';
import onboardingService, { BusinessProfile } from '../../services/onboardingService';

interface GoalsStepProps {
  profile: BusinessProfile | null;
  updateProfile: (updates: Partial<BusinessProfile>) => void;
  onNext: () => void;
  onBack: () => void;
}

const GoalsStep: React.FC<GoalsStepProps> = ({
  profile,
  updateProfile,
  onNext,
  onBack
}) => {
  const { t } = useTranslation();

  const GOALS = [
    {
      id: 'sales',
      icon: ShoppingCart,
      title: t('onboarding.goals.sales', 'More Sales'),
      description: t('onboarding.goals.salesDesc', 'Increase conversions and revenue'),
      color: 'from-green-500 to-emerald-500'
    },
    {
      id: 'leads',
      icon: Users,
      title: t('onboarding.goals.leads', 'More Leads'),
      description: t('onboarding.goals.leadsDesc', 'Capture qualified contacts'),
      color: 'from-blue-500 to-cyan-500'
    },
    {
      id: 'followers',
      icon: TrendingUp,
      title: t('onboarding.goals.followers', 'More Followers'),
      description: t('onboarding.goals.followersDesc', 'Grow on social media'),
      color: 'from-purple-500 to-pink-500'
    },
    {
      id: 'awareness',
      icon: Eye,
      title: t('onboarding.goals.awareness', 'Brand Awareness'),
      description: t('onboarding.goals.awarenessDesc', 'Increase brand recognition'),
      color: 'from-orange-500 to-amber-500'
    },
    {
      id: 'traffic',
      icon: MousePointerClick,
      title: t('onboarding.goals.traffic', 'More Traffic'),
      description: t('onboarding.goals.trafficDesc', 'Attract visitors to your website'),
      color: 'from-red-500 to-rose-500'
    },
    {
      id: 'engagement',
      icon: Megaphone,
      title: t('onboarding.goals.engagement', 'More Engagement'),
      description: t('onboarding.goals.engagementDesc', 'Increase audience interaction'),
      color: 'from-indigo-500 to-violet-500'
    }
  ];

  const [selectedGoals, setSelectedGoals] = useState<string[]>(
    profile?.marketing_goals || []
  );
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    if (profile?.marketing_goals) {
      setSelectedGoals(profile.marketing_goals);
    }
  }, [profile]);

  const toggleGoal = (goalId: string) => {
    setSelectedGoals(prev =>
      prev.includes(goalId)
        ? prev.filter(g => g !== goalId)
        : [...prev, goalId]
    );
  };

  const handleSave = async () => {
    if (selectedGoals.length === 0) {
      toast.error(t('onboarding.goals.selectError', 'Select at least one goal'));
      return;
    }

    setIsSaving(true);
    try {
      const updatedProfile = await onboardingService.saveGoals(selectedGoals);
      updateProfile(updatedProfile);
      toast.success(t('onboarding.goals.saved', 'Goals saved!'));
      onNext();
    } catch (error) {
      console.error('Error saving goals:', error);
      toast.error(t('onboarding.goals.saveError', 'Error saving. Please try again.'));
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="p-5 sm:p-12">
      {/* Header */}
      <div className="text-center mb-8">
        <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-gradient-to-br from-green-500 to-emerald-500 mb-4 shadow-lg">
          <Target className="h-8 w-8 text-white" />
        </div>
        <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">
          {t('onboarding.goals.title', 'What are your marketing goals?')}
        </h2>
        <p className="text-gray-600 dark:text-gray-400">
          {t('onboarding.goals.subtitle', 'Select all the goals you want to achieve')}
        </p>
      </div>

      {/* Goals Grid */}
      <div className="max-w-2xl mx-auto">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          {GOALS.map((goal) => {
            const Icon = goal.icon;
            const isSelected = selectedGoals.includes(goal.id);

            return (
              <button
                key={goal.id}
                type="button"
                onClick={() => toggleGoal(goal.id)}
                className={`
                  relative p-5 rounded-xl border-2 text-left transition-all duration-300
                  ${isSelected
                    ? 'border-primary-500 bg-primary-50 dark:bg-primary-900/20 shadow-lg shadow-primary-500/20'
                    : 'border-gray-200 dark:border-gray-600 hover:border-gray-300 dark:hover:border-gray-500 hover:shadow-md'
                  }
                `}
              >
                {/* Checkmark */}
                {isSelected && (
                  <div className="absolute top-3 right-3">
                    <div className="w-6 h-6 rounded-full bg-primary-500 flex items-center justify-center">
                      <Check className="h-4 w-4 text-white" />
                    </div>
                  </div>
                )}

                <div className="flex items-start space-x-4">
                  <div className={`flex-shrink-0 w-12 h-12 rounded-xl bg-gradient-to-br ${goal.color} flex items-center justify-center shadow-lg`}>
                    <Icon className="h-6 w-6 text-white" />
                  </div>
                  <div>
                    <div className={`font-semibold ${isSelected ? 'text-primary-700 dark:text-primary-300' : 'text-gray-900 dark:text-white'}`}>
                      {goal.title}
                    </div>
                    <div className="text-sm text-gray-500 dark:text-gray-400">
                      {goal.description}
                    </div>
                  </div>
                </div>
              </button>
            );
          })}
        </div>

        {/* Selection count */}
        <div className="mt-6 text-center">
          <span className={`text-sm ${selectedGoals.length > 0 ? 'text-primary-600 dark:text-primary-400' : 'text-gray-500 dark:text-gray-400'}`}>
            {selectedGoals.length === 0
              ? t('onboarding.goals.noneSelected', 'No goals selected')
              : t('onboarding.goals.selectedCount', '{{count}} goal(s) selected', { count: selectedGoals.length })
            }
          </span>
        </div>
      </div>

      {/* Navigation */}
      <div className="flex justify-between items-center mt-10 pt-6 border-t border-gray-200 dark:border-gray-700">
        <button
          onClick={onBack}
          className="flex items-center px-4 py-2 text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white transition-colors"
        >
          <ArrowLeft className="h-4 w-4 mr-2" />
          {t('common.back', 'Back')}
        </button>

        <button
          onClick={handleSave}
          disabled={isSaving || selectedGoals.length === 0}
          className="flex items-center px-6 py-3 bg-gradient-to-r from-primary-600 to-purple-600 text-white rounded-xl font-semibold shadow-lg shadow-primary-500/30 hover:shadow-xl disabled:opacity-50 disabled:cursor-not-allowed transition-all"
        >
          {isSaving ? (
            <>
              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              {t('common.saving', 'Saving...')}
            </>
          ) : (
            <>
              {t('common.next', 'Continue')}
              <ArrowRight className="h-4 w-4 ml-2" />
            </>
          )}
        </button>
      </div>
    </div>
  );
};

export default GoalsStep;
